import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useNotification } from '../../../hooks/useNotification';
import { Eye, EyeOff, CheckCircle, AlertCircle, X, ArrowLeft } from 'lucide-react';

// Modern Toast Notification Component
const Notification = ({ type, message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const isSuccess = type === 'success';

  return (
    <div className="fixed top-6 right-6 z-50 transform transition-all duration-500 ease-out animate-in slide-in-from-right">
      <div className={`
        backdrop-blur-md rounded-2xl p-4 shadow-2xl border max-w-sm
        ${isSuccess 
          ? 'bg-green-500/90 border-green-400/30 text-white' 
          : 'bg-red-500/90 border-red-400/30 text-white'
        }
      `}>
        <div className="flex items-center gap-3">
          <div className={`
            p-1.5 rounded-full 
            ${isSuccess ? 'bg-white/20' : 'bg-white/20'}
          `}>
            {isSuccess ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
          </div>
          
          <div className="flex-1">
            <p className="text-sm font-medium">
              {message}
            </p>
          </div>
          
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Progress bar */}
        <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white/40 rounded-full animate-pulse"
            style={{
              animation: 'shrink 4s linear forwards'
            }}
          />
        </div>
      </div>
      
      <style jsx>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        .animate-in {
          animation: slideIn 0.3s ease-out;
        }
        .slide-in-from-right {
          transform: translateX(100%);
        }
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

const LoginForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [notification, setNotification] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();
  const { showSuccess: originalShowSuccess, showError: originalShowError } = useNotification();

  // Get the intended destination or default to dashboard
  const from = location.state?.from?.pathname || '/admin/dashboard';

  // Custom notification functions that use our component
  const showSuccess = (message) => {
    setNotification({ type: 'success', message });
    originalShowSuccess(message);
  };

  const showError = (message) => {
    setNotification({ type: 'error', message });
    originalShowError(message);
  };

  const closeNotification = () => {
    setNotification(null);
  };

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      showError('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      console.log('Submitting login with data:', { email: formData.email });
      
      const result = await login({
        email: formData.email,
        password: formData.password
      });
      
      console.log('Login result:', result);
      
      if (result.success) {
        showSuccess('Login successful! Welcome back.');
        navigate(from, { replace: true });
      } else {
        showError(result.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      showError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToHome = () => {
    window.location.href = '/';
  };

  return (
    <>
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={closeNotification}
        />
      )}
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-amber-50/30 to-gray-50 flex items-center justify-center p-6 relative overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-amber-200/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-amber-300/20 rounded-full blur-3xl"></div>
        </div>

        {/* Back to Home Button */}
        <button
          onClick={handleBackToHome}
          className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm text-gray-700 rounded-lg hover:bg-white transition shadow-sm border border-gray-200"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-medium">Back to Home</span>
        </button>

        <div className="w-full max-w-md relative z-10">
          {/* Header with Logo */}
          <div className="text-center mb-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src="/images/logo.png" 
                alt="Dewanta Villas Management" 
                className="h-16 w-auto"
              />
            </div>
            
            {/* <h1 className="text-3xl font-bold text-gray-900 mb-2">Dewanta Villas Management</h1> */}
            <p className="text-gray-600 text-base mb-1">Internal Admin Portal</p>
            <p className="text-sm text-amber-600 font-medium">Staff Access Only</p>
          </div>

          {/* Login Card */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Input */}
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-semibold text-gray-700 block">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@dewantavillas.com"
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all text-sm bg-white"
                  required
                />
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-semibold text-gray-700 block">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    className="w-full px-4 py-3.5 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all text-sm bg-white"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Info Text */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-xs text-amber-800 leading-relaxed">
                  <span className="font-semibold">Note:</span> This page is for internal staff access only. If you experience login issues, please contact IT Support.
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-3.5 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Signing in...
                  </div>
                ) : (
                  'Sign In to Dashboard'
                )}
              </button>
            </form>

            {/* Footer Note */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-center text-gray-500">
                © 2024 Dewanta Villas Management. All rights reserved.
              </p>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Need help?{' '}
              <a href="mailto:support@dewantavillas.com" className="text-amber-600 hover:text-amber-700 font-medium">
                Contact IT Support
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginForm;